// src/hooks/useFetch.js - load data for a page, with a retry that really retries (audit FL-07).
//
//   const { data, error, isLoading, reload } = useFetch(() => api.get(...).then(r => r.data), id);
//
// * every (re)load clears the previous error and shows the loading state;
// * `reload({ silent: true })` refreshes in the background (keeps the current data on screen);
// * a response that arrives after a newer request started is ignored.
import { useCallback, useEffect, useRef, useState } from 'react';
import { toApiError } from '../services/api';

export default function useFetch(loader, key) {
  const [state, setState] = useState({ data: null, error: null, isLoading: true });
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const requestId = useRef(0);

  const reload = useCallback(async ({ silent = false } = {}) => {
    const id = ++requestId.current;
    setState((s) => ({ data: s.data, error: null, isLoading: !silent || s.data === null }));
    try {
      const data = await loaderRef.current();
      if (id === requestId.current) setState({ data, error: null, isLoading: false });
      return data;
    } catch (err) {
      if (id === requestId.current) setState((s) => ({ data: s.data, error: toApiError(err), isLoading: false }));
      return undefined;
    }
  }, []);

  const setData = useCallback((updater) => {
    setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  }, []);

  useEffect(() => {
    reload();
  }, [key, reload]);

  return { ...state, reload, setData };
}
